import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, Checkbox, Field, Input, Select, StatusPill, Switch } from '@bb/ui';
import { ApiError, type AdminApi, type AlertObjectType, type AlertRuleInput, type AlertRuleView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { formatWhen } from '../segments/SegmentsListPage.tsx';

type Metric = AlertRuleView['metric'];
type Condition = AlertRuleView['condition'];

const METRICS: Record<Metric, string> = { dispatched: 'Dispatched messages', accepted: 'Accepted by the provider', failed_rate: 'Failure rate' };
const CONDITIONS: Record<Condition, string> = { absent_for: 'Stops for the whole window', below: 'Falls below', above: 'Rises above' };
const DAYS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const EDIT: Record<AlertObjectType, string> = { campaign: 'campaigns.edit', canvas: 'canvas.edit' };
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

export const formatWindow = (minutes: number) => (minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`);
const formatValue = (metric: Metric, value: number) => (metric === 'failed_rate' ? `${Math.round(value * 1000) / 10}%` : String(value));

/** One line a marketer reads: "No dispatched messages for 15 min", "Failure rate above 20% over 1 h". */
export function describeRule(rule: Pick<AlertRuleView, 'metric' | 'condition' | 'threshold' | 'windowMinutes'>): string {
  const window = formatWindow(rule.windowMinutes);
  if (rule.condition === 'absent_for') return `No ${METRICS[rule.metric].toLowerCase()} for ${window}`;
  return `${METRICS[rule.metric]} ${rule.condition} ${formatValue(rule.metric, rule.threshold)} over ${window}`;
}

const describeHours = (hours: AlertRuleView['activeHours']) => (hours ? `${hours.from}–${hours.to} ${hours.days ? hours.days.map((d) => DAYS[d]).join(', ') : 'every day'}, environment time zone` : 'Around the clock');

function RuleItem({ environmentId, rule, canEdit }: { environmentId: string; rule: AlertRuleView; canEdit: boolean }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['alerts', environmentId] });
  const toggle = useMutation({ mutationFn: (enabled: boolean) => api.updateAlertRule(environmentId, rule.id, { enabled }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api.deleteAlertRule(environmentId, rule.id), onSuccess: refresh });
  const test = useMutation({ mutationFn: () => api.testAlertRule(environmentId, rule.id) });
  const [label, tone] = !rule.enabled ? (['Off', 'neutral'] as const) : rule.state === 'firing' ? (['Firing', 'danger'] as const) : (['OK', 'success'] as const);
  const error = toggle.error ?? remove.error ?? test.error;
  return (
    <li>
      <article aria-label={`Alert ${rule.name}`}>
        <h3>
          {rule.name} <StatusPill tone={tone}>{label}</StatusPill>
        </h3>
        <p>{describeRule(rule)}</p>
        <p className="hint">
          {describeHours(rule.activeHours)}. Notifies: {rule.notify.map((c) => (c === 'slack' ? 'Slack' : 'dashboard')).join(', ')}.
          {rule.lastEvaluatedAt ? ` Last checked ${formatWhen(rule.lastEvaluatedAt)}: ${formatValue(rule.metric, rule.lastValue ?? 0)}.` : ' Not checked yet.'}
        </p>
        {canEdit && (
          <div className="toolbar">
            <Switch label="Enabled" checked={rule.enabled} onCheckedChange={(enabled) => toggle.mutate(enabled)} disabled={toggle.isPending} />
            {rule.notify.includes('slack') && (
              <Button variant="secondary" onClick={() => test.mutate()} disabled={test.isPending}>
                Send a test to Slack
              </Button>
            )}
            <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
              Delete alert
            </Button>
          </div>
        )}
        {test.data && <p role="status">{test.data.sent.includes('slack') ? 'Test sent to Slack.' : 'Slack is not configured on the server; nothing was sent.'}</p>}
        {error && <p role="alert">{errorText(error)}</p>}
      </article>
    </li>
  );
}

interface Draft {
  name: string;
  metric: Metric;
  condition: Condition;
  threshold: string;
  windowMinutes: string;
  limitHours: boolean;
  from: string;
  to: string;
  slack: boolean;
}
const EMPTY: Draft = { name: '', metric: 'dispatched', condition: 'absent_for', threshold: '', windowMinutes: '15', limitHours: false, from: '09:00', to: '21:00', slack: false };

function NewRuleForm({ environmentId, objectType, objectId }: { environmentId: string; objectType: AlertObjectType; objectId: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(EMPTY);
  const [invalid, setInvalid] = useState<string | null>(null);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const create = useMutation({
    mutationFn: (body: AlertRuleInput) => api.createAlertRule(environmentId, body),
    onSuccess: () => {
      setDraft(EMPTY);
      return queryClient.invalidateQueries({ queryKey: ['alerts', environmentId] });
    },
  });
  const percent = draft.metric === 'failed_rate';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const window = Number(draft.windowMinutes);
    const threshold = draft.condition === 'absent_for' ? 0 : Number(draft.threshold);
    if (!draft.name.trim()) return setInvalid('Name the alert.');
    if (!Number.isInteger(window) || window < 5 || window > 1440) return setInvalid('The window is 5 to 1440 minutes.');
    if (draft.condition !== 'absent_for' && (draft.threshold === '' || !(threshold >= 0) || (percent && threshold > 100))) return setInvalid(percent ? 'The threshold is a percentage from 0 to 100.' : 'The threshold is a number of messages.');
    setInvalid(null);
    create.mutate({
      objectType,
      objectId,
      name: draft.name.trim(),
      metric: draft.metric,
      condition: draft.condition,
      threshold: percent ? threshold / 100 : threshold,
      windowMinutes: window,
      activeHours: draft.limitHours ? { from: draft.from, to: draft.to } : null,
      notify: draft.slack ? ['dashboard', 'slack'] : ['dashboard'],
      enabled: true,
    });
  };
  return (
    <form aria-label="New alert" onSubmit={submit}>
      <h3>New alert</h3>
      <Field label="Alert name">
        <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field label="Metric">
        <Select value={draft.metric} onChange={(e) => set({ metric: e.target.value as Metric })} options={Object.entries(METRICS).map(([value, label]) => ({ value, label }))} />
      </Field>
      <Field label="Condition">
        <Select value={draft.condition} onChange={(e) => set({ condition: e.target.value as Condition })} options={Object.entries(CONDITIONS).map(([value, label]) => ({ value, label }))} />
      </Field>
      {draft.condition !== 'absent_for' && (
        <Field label={percent ? 'Threshold, %' : 'Threshold, messages'}>
          <Input type="number" min={0} step="any" value={draft.threshold} onChange={(e) => set({ threshold: e.target.value })} />
        </Field>
      )}
      <Field label="Window, minutes" hint="The metric is summed over this sliding window; data arrives with about a minute of delay.">
        <Input type="number" min={5} max={1440} value={draft.windowMinutes} onChange={(e) => set({ windowMinutes: e.target.value })} />
      </Field>
      <Checkbox label="Only during active hours" checked={draft.limitHours} onCheckedChange={(limitHours) => set({ limitHours })} />
      {draft.limitHours && (
        <>
          <Field label="From">
            <Input type="time" value={draft.from} onChange={(e) => set({ from: e.target.value })} />
          </Field>
          <Field label="To">
            <Input type="time" value={draft.to} onChange={(e) => set({ to: e.target.value })} />
          </Field>
        </>
      )}
      <Checkbox label="Also notify Slack" checked={draft.slack} onCheckedChange={(slack) => set({ slack })} />
      {(invalid ?? create.error) && <p role="alert">{invalid ?? errorText(create.error)}</p>}
      <Button type="submit" variant="primary" disabled={create.isPending}>
        Add alert
      </Button>
    </form>
  );
}

/** Past and current alerts of the object, newest first. */
export function AlertHistory({ environmentId, objectId }: { environmentId: string; objectId: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const events = useQuery({ queryKey: ['alerts', environmentId, 'events', objectId], queryFn: () => api.alertEvents(environmentId, objectId) });
  if (!events.data?.items.length) return <p className="hint">No alerts have fired.</p>;
  return (
    <ol aria-label="Alert history">
      {events.data.items.map((e) => (
        <li key={e.id}>
          <strong>{e.ruleName}</strong> fired {formatWhen(e.firedAt)} — {e.resolvedAt ? `resolved ${formatWhen(e.resolvedAt)}` : 'still firing'}. <span className="hint">Notified: {e.notified.join(', ')}.</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Monitoring of a campaign or Canvas (BB-107): rules over dispatched / accepted / failure rate with a sliding window,
 * their current state and history. The worker evaluates them every minute; editors of the object manage them.
 */
export function AlertRulesPanel({ environmentId, objectType, objectId }: { environmentId: string; objectType: AlertObjectType; objectId: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const permissions = usePermissions();
  const canEdit = permissions.has(EDIT[objectType]);
  const rules = useQuery({ queryKey: ['alerts', environmentId, objectType, objectId], queryFn: () => api.alertRules(environmentId, objectType, objectId) });
  return (
    <section aria-label="Monitoring" className="panel">
      <h2>Monitoring</h2>
      {rules.isError && <p role="alert">{errorText(rules.error)}</p>}
      {rules.data &&
        (rules.data.items.length ? (
          <ul aria-label="Alert rules" className="plain-list">
            {rules.data.items.map((rule) => (
              <RuleItem key={rule.id} environmentId={environmentId} rule={rule} canEdit={canEdit} />
            ))}
          </ul>
        ) : (
          <p className="hint">No alerts yet. An alert tells you when sending stops or failures spike.</p>
        ))}
      {canEdit && <NewRuleForm environmentId={environmentId} objectType={objectType} objectId={objectId} />}
      <h3>Alert history</h3>
      <AlertHistory environmentId={environmentId} objectId={objectId} />
    </section>
  );
}
